import { Loader2, MapPin, Search, Sparkles, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useEffect, useRef, useState } from "react";
import type { PlaceCategory } from "@shared/types";
import { t } from "@/i18n";
import { get } from "@/lib/api";
import { CATEGORY } from "@/lib/meta";
import { useUI } from "@/lib/store";
import type { Bundle } from "@/lib/trip";
import type { PlaceDraft } from "../places/AddPlaceDialog";
import { cx } from "../ui";

interface GeoResult {
  name: string;
  displayName: string;
  lat: number;
  lng: number;
  category: PlaceCategory;
  region: string | null;
}

export function MapSearch({ b, onPickExternal, className }: { b: Bundle; onPickExternal: (d: PlaceDraft) => void; className?: string }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [res, setRes] = useState<GeoResult[]>([]);
  const [loading, setLoading] = useState(false);
  const selectPlace = useUI((s) => s.selectPlace);
  const focusMap = useUI((s) => s.focusMap);
  const box = useRef<HTMLDivElement>(null);
  const aiOn = Boolean(useAuth().config?.aiEnabled);

  const looksUrl = (x: string) => x.startsWith("http://") || x.startsWith("https://");
  const isUrl = looksUrl(q.trim());
  useEffect(() => {
    const s = q.trim();
    if (s.length < 3 || s.startsWith("http://") || s.startsWith("https://")) {
      setRes([]);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const cc = (b.trip.settings.countryCodes ?? []).join(",");
        const c = b.trip.settings.mapCenter ?? [16, 106];
        setRes(await get<GeoResult[]>(`/api/geo/search?q=${encodeURIComponent(s)}&lat=${c[0]}&lng=${c[1]}${cc ? `&cc=${cc}` : ""}`));
      } catch {
        setRes([]);
      } finally {
        setLoading(false);
      }
    }, 650);
    return () => clearTimeout(timer);
  }, [q, b.trip.settings]);

  useEffect(() => {
    const h = (e: MouseEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const local = q.trim().length >= 2 ? b.places.filter((p) => `${p.name} ${p.nameLocal ?? ""} ${p.region ?? ""}`.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 5) : [];

  return (
    <div ref={box} className={cx("relative", className)}>
      <div className="glass flex h-11 items-center gap-2 rounded-2xl px-3">
        {loading ? <Loader2 size={17} className="animate-spin text-muted" /> : <Search size={17} className="text-muted" />}
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && isUrl && aiOn) {
              onPickExternal({ smartText: q.trim() });
              setOpen(false);
              setQ("");
            }
          }}
          placeholder={t("Найти место или вставить ссылку…")}
          className="min-w-0 flex-1 bg-transparent text-[14px] font-medium outline-none placeholder:text-[var(--muted)]"
        />
        {q && (
          <button onClick={() => setQ("")} aria-label={t("Очистить")} className="text-muted">
            <X size={16} />
          </button>
        )}
      </div>
      {open && (local.length > 0 || res.length > 0 || (aiOn && q.trim().length >= 3)) && (
        <div className="animate-in scroll-thin absolute inset-x-0 z-[1200] mt-1.5 max-h-[60vh] overflow-y-auto rounded-2xl border border-[var(--border)] bg-[var(--surface-solid)] p-1 shadow-[var(--shadow-lg)]">
          {local.length > 0 && <div className="px-3 pt-2 pb-1 text-[11px] font-extrabold tracking-wider text-muted uppercase">{t("В поездке")}</div>}
          {local.map((p) => (
            <button
              key={p.id}
              className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left hover:bg-[var(--surface-2)]"
              onClick={() => {
                selectPlace(p.id);
                focusMap({ bounds: [[p.lat, p.lng], [p.lat, p.lng]] });
                setOpen(false);
              }}
            >
              <span>{CATEGORY[p.category].emoji}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold">{p.name}</span>
                <span className="block truncate text-[12px] text-muted">{p.region}</span>
              </span>
            </button>
          ))}
          {res.length > 0 && <div className="px-3 pt-2 pb-1 text-[11px] font-extrabold tracking-wider text-muted uppercase">{t("Найдено на карте — нажмите, чтобы добавить")}</div>}
          {res.map((r, i) => (
            <button
              key={`${r.lat},${r.lng},${i}`}
              className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left hover:bg-[var(--surface-2)]"
              onClick={() => {
                focusMap({ center: [r.lat, r.lng], zoom: 15 });
                onPickExternal({ name: r.name, lat: r.lat, lng: r.lng, category: r.category, region: r.region, address: r.displayName });
                setOpen(false);
                setQ("");
              }}
            >
              <MapPin size={16} className="shrink-0 text-[var(--accent)]" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold">{r.name}</span>
                <span className="block truncate text-[12px] text-muted">{r.displayName}</span>
              </span>
            </button>
          ))}
          {aiOn && q.trim().length >= 3 && b.trip.myRole !== "viewer" && (
            <button
              className="mt-1 flex w-full items-center gap-2.5 rounded-xl border-t border-[var(--border)] px-3 py-2.5 text-left hover:bg-[var(--accent-soft)]"
              onClick={() => {
                onPickExternal({ smartText: q.trim() });
                setOpen(false);
                setQ("");
              }}
            >
              <Sparkles size={16} className="shrink-0 text-[var(--accent)]" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-[var(--accent)]">{isUrl ? t("Разобрать ссылку с ИИ") : t("Добавить с помощью ИИ")}</span>
                <span className="block truncate text-[12px] text-muted">{q.trim()}</span>
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
